import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Trophy } from "lucide-react";
import { formatPoints } from "@/lib/format";

export default function Rankings() {
    const { t } = useI18n();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/leaderboard").then((r) => {
            setRows(r.data);
            setLoading(false);
        });
    }, []);

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12" data-testid="rankings-page">
            <div className="flex items-center gap-3 mb-2 text-p2w-amber">
                <Trophy className="w-5 h-5" />
                <span className="text-xs uppercase tracking-[0.3em] font-sora font-bold">{t("rankings.title")}</span>
            </div>
            <h1 className="font-sora font-extrabold text-4xl sm:text-5xl tracking-tighter">{t("rankings.title")}</h1>
            <p className="mt-2 text-p2w-gray">{t("rankings.sub")}</p>

            <div className="mt-10 overflow-hidden rounded-2xl border border-p2w-line bg-p2w-surface">
                <div className="hidden sm:grid grid-cols-12 px-5 py-3 text-[10px] uppercase tracking-widest text-p2w-gray border-b border-p2w-line bg-p2w-elevated">
                    <div className="col-span-1">{t("rankings.rank")}</div>
                    <div className="col-span-5">{t("rankings.user")}</div>
                    <div className="col-span-2 text-right">{t("rankings.level")}</div>
                    <div className="col-span-2 text-right">{t("rankings.predictions")}</div>
                    <div className="col-span-2 text-right">{t("rankings.points")}</div>
                </div>
                {loading
                    ? Array.from({ length: 8 }).map((_, i) => (
                          <div key={i} className="h-14 border-b border-p2w-line animate-pulse bg-p2w-surface/60" />
                      ))
                    : rows.map((r) => (
                          <div
                              key={r.user_id}
                              className="grid grid-cols-12 px-5 py-4 items-center border-b border-p2w-line hover:bg-p2w-elevated/60 transition"
                              data-testid={`rank-row-${r.rank}`}
                          >
                              <div className="col-span-1 flex items-center">
                                  <RankBadge rank={r.rank} />
                              </div>
                              <div className="col-span-5 flex items-center gap-3">
                                  <Avatar className="w-9 h-9 ring-1 ring-p2w-line">
                                      <AvatarImage src={r.avatar_url} alt={r.username} />
                                      <AvatarFallback className="bg-p2w-purple text-white text-xs">
                                          {(r.username || "?").slice(0, 2).toUpperCase()}
                                      </AvatarFallback>
                                  </Avatar>
                                  <div>
                                      <div className="font-semibold text-white">@{r.username}</div>
                                      <div className="text-xs text-p2w-gray number-tabular">
                                          {r.win_rate}% aciertos
                                      </div>
                                  </div>
                              </div>
                              <div className="col-span-2 text-right">
                                  <span className="inline-flex px-2.5 py-1 rounded-md text-xs font-bold bg-p2w-purple/15 text-p2w-purple border border-p2w-purple/30 number-tabular">
                                      Lv {r.level}
                                  </span>
                              </div>
                              <div className="col-span-2 text-right text-white number-tabular">{r.predictions}</div>
                              <div className="col-span-2 text-right font-sora font-bold text-p2w-green number-tabular">
                                  {formatPoints(r.points_balance)}
                              </div>
                          </div>
                      ))}
            </div>
        </div>
    );
}

function RankBadge({ rank }) {
    const cls =
        rank === 1
            ? "bg-p2w-green text-p2w-green-fg shadow-glow-green"
            : rank === 2
            ? "bg-white/80 text-p2w-bg"
            : rank === 3
            ? "bg-p2w-amber text-p2w-bg"
            : "bg-p2w-elevated text-p2w-gray border border-p2w-line";
    return (
        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-sora font-bold text-sm number-tabular ${cls}`}>
            {rank}
        </div>
    );
}
